import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, formatRelative } from "@/i18n";

const diversify = (items, n) => {
  const seen = new Set(), first = [], rest = [];
  items.forEach((p) => (seen.has(p.handle) ? rest : (seen.add(p.handle), first)).push(p));
  return [...first, ...rest].slice(0, n);
};

function PulseMini({ p }) {
  const { lang } = useI18n();
  const headline = (lang === "ar" && p.headline_ar) || p.ai_headline;
  return (
    <a data-testid="pulse-strip-card" href={p.url} target="_blank" rel="noopener noreferrer"
      className="snap-start shrink-0 w-72 rounded-xl bg-[#0d0f16] ring-1 ring-white/10 hover:ring-violet-500/60 hover:-translate-y-0.5 transition-[transform,box-shadow] p-4 flex flex-col text-slate-100">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-cyan-500 inline-flex items-center justify-center text-xs font-bold">{p.author_name?.[0]}</span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold truncate">{p.author_name}</div>
          <div className="text-[11px] text-slate-400 truncate" dir="ltr">@{p.handle}</div>
        </div>
        <span className="font-black text-sm">𝕏</span>
      </div>
      <p className="mt-3 text-sm font-semibold leading-snug line-clamp-3"><Quote size={12} className="inline -mt-1 me-1 text-violet-400" />{headline}</p>
      <span className="mt-auto pt-3 text-[11px] text-slate-400">{formatRelative(p.created_at, lang)}</span>
    </a>
  );
}

export function PulseStrip() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["x-pulse-strip"], queryFn: async () => (await api.get("/x-pulse?limit=30")).data, refetchInterval: 300000 });
  const items = diversify(data?.items || [], 8);
  if (!items.length) return null;
  return (
    <section data-testid="pulse-strip" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display font-bold text-base md:text-lg inline-flex items-center gap-2">
          <span className="inline-flex w-6 h-6 rounded bg-black text-white items-center justify-center text-xs font-black">𝕏</span>{t("xp.title")}
        </h2>
        <Link data-testid="pulse-strip-view-all" to="/x-pulse" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1 text-sm font-semibold text-violet-600 hover:text-violet-800">
          {t("section.viewAll")} <ArrowRight size={14} className="rtl:rotate-180" />
        </Link>
      </div>
      <div className="mt-4 flex gap-3 overflow-x-auto snap-x pb-2 [scrollbar-width:thin]">
        {items.map((p) => <PulseMini key={p.id} p={p} />)}
      </div>
    </section>
  );
}
